<template>
    <Modal
        id="create-tax-modal"
        ref="createTaxModal"
        v-model:open="open"
        class="relative"
        :title="$t('create_tax')"
        @cancel="cancel"
        @submit="submit"
    >
        <div class="h-auto w-full">
            <div
                v-if="showErrorMessage"
                class="sticky top-0 left-0 z-50 h-20 w-full rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700"
            ></div>
            <TextField
                v-model="form.name"
                :label="$t('tax_name')"
                name="name"
                :required="true"
            />

            <SelectField
                v-model="form.issuer_country"
                :label="$t('issuer_country')"
                name="issuer_country"
                :required="true"
                :options="countries"
            />

            <SelectField
                v-model="form.investor_country"
                :label="$t('investor_country')"
                name="investor_country"
                :required="true"
                :options="countries"
            />

            <SelectField
                v-model="form.platform_names"
                :label="$t('platforms')"
                name="platform_names"
                :options="platformSelectOptions"
                :multiple="true"
                :on-search="loadPlatformNameOptions"
            />

            <SelectField
                v-model="form.asset_types"
                :label="$t('asset_types')"
                name="asset_types"
                :options="assetTypesOptions"
                :multiple="true"
            />

            <SelectField
                v-model="form.target"
                :label="$t('target')"
                name="target"
                :required="true"
                :options="targetOptions"
            />

            <TaxDetails @open-formula-modal="openFormulaModal" />

            <TaxFormulaButtons @open-formula-modal="openFormulaModal" />
        </div>

        <template #actions>
            <button class="btn bg-primary-accent-500 text-white">
                {{ $t('Create') }}
            </button>
        </template>
    </Modal>
    <CreateFormulaModal
        v-if="formulaModalOpen.open"
        v-model:open="formulaModalOpen.open"
        :type="formulaModalOpen.type"
        :formula="form.formula_amount"
        :tax-details="taxDetailPlaceholders"
        :name="formulaModalOpen.name"
        @cancel="formulaModalOpen.open = false"
        @submit="formulaModalSubmit"
    />
</template>

<script setup lang="ts">
import Modal from '@src/components/Modal.vue'
import SelectField from '@src/components/SelectField.vue'
import TextField from '@src/components/TextField.vue'
import { getFormattedTaxDetailName } from '@src/composables/taxes/taxes'
import { TaxDetails as TaxDetailsType } from '@src/types/Tax'
import { getPlatforms } from '@src/utils/api/platforms'
import { useCountryStore } from '@src/utils/stores/countries'
import { useTaxesStore } from '@src/utils/stores/taxes'
import { useVModels } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { onMounted, reactive, ref } from 'vue'
import CreateFormulaModal from './CreateFormulaModal.vue'
import TaxDetails from './TaxDetails.vue'
import TaxFormulaButtons from './TaxFormulaButtons.vue'

const props = defineProps<{
    open: boolean
}>()

const emit = defineEmits(['cancel', 'submit'])

const { open } = useVModels(props, emit)

const countryStore = useCountryStore()
const taxStore = useTaxesStore()
const { taxForm: form, taxDetails } = storeToRefs(taxStore)

const platformSelectOptions = ref<string[]>([])
const countries = ref<{ label: string; value: string | null }[]>([])
const showErrorMessage = ref(false)
const taxDetailPlaceholders = ref<string[]>([])
const formulaModalOpen = reactive<{
    open: boolean
    type: 'amount' | 'rate' | 'tax_detail_rate' | 'tax_detail_amount'
    name?: string
}>({
    open: false,
    type: 'amount',
    name: '',
})

const cancel = () => {
    emit('cancel')
}

const submit = () => {
    taxDetails.value.forEach((detail: TaxDetailsType) => {
        const snakeCaseName = detail.name
            .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
            .replace(/[\s-]+/g, '_')
            .toLowerCase()
        form.value.tax_details[snakeCaseName as string] = {
            name: detail.name,
            rate: detail.rate,
            amount: detail.amount,
        }
    })
    emit('submit', form.value)
}

const targetOptions = [
    {
        label: 'Natural Profile',
        value: 'natural',
    },
    {
        label: 'Legal Profile',
        value: 'legal',
    },
]

const assetTypesOptions = [
    {
        label: 'Crowd Investing',
        value: 'CI',
    },
    {
        label: 'Crowd Funding',
        value: 'CF',
    },
    {
        label: 'RISE',
        value: 'RISE',
    },
    {
        label: 'Emission',
        value: 'EMISSION',
    },
    {
        label: 'Stock Emission',
        value: 'STOCK_EMISSION',
    },
    {
        label: 'ICO',
        value: 'ICO',
    },
]

const formulaModalSubmit = (formula: string) => {
    if (formulaModalOpen.type === 'amount') {
        form.value.formula_amount = formula
    } else if (formulaModalOpen.type === 'rate') {
        form.value.formula_rate = formula
    } else if (formulaModalOpen.type === 'tax_detail_rate') {
        const taxDetail = taxDetails.value.find(
            (detail) =>
                getFormattedTaxDetailName(detail.name) === formulaModalOpen.name
        )
        taxDetail && (taxDetail.rate = formula)
    } else if (formulaModalOpen.type === 'tax_detail_amount') {
        const taxDetail = taxDetails.value.find(
            (detail) =>
                getFormattedTaxDetailName(detail.name) === formulaModalOpen.name
        )
        taxDetail && (taxDetail.amount = formula)
    }
}

const openFormulaModal = ({
    type,
    name,
}: {
    type: 'amount' | 'rate' | 'tax_detail_rate' | 'tax_detail_amount'
    name?: string
}) => {
    taxDetails.value.forEach((detail: TaxDetailsType) => {
        taxDetailPlaceholders.value.push(getFormattedTaxDetailName(detail.name))
    })
    formulaModalOpen.open = true
    formulaModalOpen.type = type
    formulaModalOpen.name = getFormattedTaxDetailName(name ?? '')
}

async function loadPlatformNameOptions(search: string) {
    const response = await getPlatforms({
        'filter[platform_name]': search,
    })
    platformSelectOptions.value = response.data.platforms
        .map((platform) => platform.platform_name as string)
        .filter(Boolean)
}

onMounted(async () => {
    const response = await getPlatforms()
    platformSelectOptions.value = response.data.platforms
        .map((platform) => platform.platform_name as string)
        .filter(Boolean)
    if (form.value.platform_names?.length) {
        platformSelectOptions.value = Array.from(
            new Set([
                ...form.value.platform_names,
                ...platformSelectOptions.value,
            ])
        )
    }

    countries.value = await countryStore.loadAndSortCountriesOptionsAsync()
})
</script>
